import {RequestHandler} from './RequestHandler';
import {StatusResponse} from '../models/StatusResponse';
import {ContentType} from './ContentType';
import {config} from '../init/config';
import {useContext, useMemo} from 'react';
import {AuthContext} from '../context/AuthContext';
import {SearchResultDTO} from '../models/dto/SearchResultDTO';
import {DownloadResponse} from '../hooks/UseDownloadFile';
import {BreadcrumbsDTO} from '../models/dto/BreadcrumbsDTO';
import {StorageObject} from '../models/StorageObject';
import {DisclaimerDTO} from '../models/dto/DisclaimerDTO';
import {LabelDTO} from '../models/dto/LabelDTO';
import {t} from 'i18next';
import {SearchFilter} from '../models/SearchFilter';
import {LENGTH_LIMIT_URL_QUERY_PARAMETER} from '../init/constants';
import {isNullOrEmpty} from '../utils';
import {Template} from '../models/Template';

const constructRequestHandler = (token: string | null, backendUrl: string | undefined) => {
    return useMemo(() => new RequestHandler(backendUrl ? backendUrl : 'dummyPath', token), [token]);
};

export const APIClient = () => {
    const {token} = useContext(AuthContext);
    const {DLS_BACKEND_URL, WTM_BACKEND_URL, DISCLAIMER_URL, LABEL_URL} = config;
    const requestHandler = constructRequestHandler(token, DLS_BACKEND_URL);
    const watermarkSvcRequestHandler = constructRequestHandler(token, WTM_BACKEND_URL);
    const disclaimerRequestHandler = constructRequestHandler(token, DISCLAIMER_URL);
    const labelRequestHandler = constructRequestHandler(token, LABEL_URL);

    const createFolder = async (
        item: StorageObject | null | undefined
    ): Promise<StatusResponse> => {
        if ((item !== undefined) && item) {
            let url = `/${config.DLS_TENANT}/folder/create`;
            return requestHandler.post<StatusResponse>(url, JSON.stringify(item), ContentType.JSON);
        }

        return {status: '400', message: t('ERROR_FOLDER_ID_MISSING')} as StatusResponse;
    };

    const uploadFile = async (
        file: File | null,
        setProgress: (progress: number) => void,
        executeFileUpload: boolean,
        thumbnail: File | null,
        item: StorageObject | null | undefined
    ): Promise<StatusResponse> => {
        if (file) {
            let formData = new FormData();
            if ((item !== undefined) && item) {
                formData.append('fullPath', item.fullPath);
                formData.append('fileName', item.name);
                formData.append('fileSize', item.totalSize.toString());
            } else {
                return {status: '400', message: t('ERROR_FILE_MISSING')} as StatusResponse;
            }

            let blobResponse = {status: '201', message: 'Created'} as StatusResponse;
            if (executeFileUpload) {
                let sasUrl = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/file/upload/sas`;
                blobResponse = await requestHandler.upload<StatusResponse>(sasUrl, formData, file, ContentType.MULTIPART_FORMDATA, setProgress);
            }

            if (blobResponse.status === '201') {
                let url = `/${config.DLS_TENANT}/file/upload`;
                await requestHandler.post<string>(url, JSON.stringify(item), ContentType.JSON)
                    .then(async (response) => {
                        if (!isNullOrEmpty(response)) {
                            await upsertThumbnail(response, thumbnail, item.metadata.thumbnailUrl);
                        }

                        return {status: '200', message: 'OK'} as StatusResponse;
                    });
            }
        }

        return {status: '400', message: t('ERROR_FILE_MISSING')} as StatusResponse;
    };

    const getStorageObjects = async (
        country: string,
        language: string,
        path = '/',
        page: number,
        sortType: string,
        sortOrder: string,
        search: string | null,
        fileId: string | null,
        fileTypes: string[] | null,
        noOfDays: number,
        isFavorite: boolean,
        isPublic: boolean,
        objectType: string,
        pageSize: number,
    ): Promise<SearchResultDTO> => {
        let searchFilter: SearchFilter = {
            country: country,
            language: language,
            path: path,
            searchParam: search,
            fileId: fileId,
            pageNumber: page,
            fileTypes: fileTypes,
            noOfDays: noOfDays,
            isFavorite: isFavorite,
            isPublic: isPublic,
            sortType: sortType,
            sortOrder: sortOrder,
            objectType: objectType,
            pageSize: pageSize,
            flexibleSearchParameters: null
        }

        let urlLength = window.location.href.length;
        if ((LENGTH_LIMIT_URL_QUERY_PARAMETER >= urlLength) && (page > 0) && (pageSize > 0)) {
            let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/search`;
            return requestHandler.post(url, searchFilter, ContentType.JSON);
        }

        return Promise.reject({
            status: '400',
            message: t('ERROR_LENGTH_LIMIT_URL_QUERY_PARAMETER_CONSTRAINT', {
                param1: LENGTH_LIMIT_URL_QUERY_PARAMETER,
                param2: urlLength
            })
        } as StatusResponse);
    };

    const getStorageObject = async (
        path = '/'
    ): Promise<StorageObject> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/resource/search?path=${path}`;
        return requestHandler.get<StorageObject>(url);
    };

    const getBreadcrumbsObjects = async (
        country: string,
        language: string,
        path = '/',
        isPublic: boolean
    ): Promise<BreadcrumbsDTO[]> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/breadcrumbs/search?country=${country}&language=${language}&path=${path}`;
        if (isPublic) {
            url += `&isPublic=${isPublic}`;
        }

        return requestHandler.get<BreadcrumbsDTO[]>(url);
    };

    const getLabelData = async (): Promise<LabelDTO[]> => {
        let url = '';
        return labelRequestHandler.get<LabelDTO[]>(url);
    };

    const getDisclaimerData = async (): Promise<DisclaimerDTO[]> => {
        let url = '';
        return disclaimerRequestHandler.get<DisclaimerDTO[]>(url);
    };

    const giveConsent = async (
        language: string,
        disclaimerId: string,
        fileId: string | null | undefined,
        isConsentGiven: boolean
    ): Promise<StatusResponse> => {
        if ((fileId !== undefined) && fileId) {
            let formData = new FormData();
            formData.append('language', language);
            formData.append('disclaimerId', disclaimerId);
            formData.append('fileId', fileId);
            formData.append('isConsentGiven', isConsentGiven.toString());

            let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/consent/give`;
            return requestHandler.post(url, formData, ContentType.MULTIPART_FORMDATA);
        }

        return {status: '400', message: t('ERROR_FILE_ID_MISSING')} as StatusResponse;
    };

    const revokeConsent = async (
        language: string,
        disclaimerId: string,
        fileId: string | null | undefined
    ): Promise<StatusResponse> => {
        if ((fileId !== undefined) && fileId) {
            let formData = new FormData();
            formData.append('language', language);
            formData.append('disclaimerId', disclaimerId);
            formData.append('fileId', fileId);

            let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/consent/revoke`;
            return requestHandler.post(url, formData, ContentType.MULTIPART_FORMDATA);
        }

        return {status: '400', message: t('ERROR_FILE_ID_MISSING')} as StatusResponse;
    };

    const downloadFile = async (
        fileId: string,
        watermarkingEnabled: boolean
    ): Promise<DownloadResponse> => {
        let params: { [key: string]: string } = {};
        params['watermarkingEnabled'] = watermarkingEnabled.toString();
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/file/${fileId}/download`;
        return requestHandler.download(url, params);
    };

    const downloadThumbnailFile = async (url: string): Promise<Blob> => {
        return requestHandler.getBlob<Blob>(url, true, false);
    };

    const downloadThumbnail = async (fileId: string): Promise<DownloadResponse> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/thumbnail/${fileId}`;
        return requestHandler.get<DownloadResponse>(url);
    };

    const downloadThumbnailUrl = async (thumbnailUrl: string): Promise<DownloadResponse> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/thumbnail/url/${thumbnailUrl}`;
        return requestHandler.get<DownloadResponse>(url);
    };

    const upsertThumbnail = async (fileId: string, file: File | null, thumbnailUrl?: string): Promise<StatusResponse> => {
        let formData = new FormData();
        if ((thumbnailUrl !== undefined) && (!isNullOrEmpty(thumbnailUrl))) {
            formData.append('thumbnailUrl', thumbnailUrl);
        }

        if (file) {
            formData.append('thumbnail', file);
        }

        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/thumbnail/upsert/${fileId}`;
        return requestHandler.put<StatusResponse>(url, formData, ContentType.MULTIPART_FORMDATA);
    };

    const getPreviewUrl = async (fileId: string): Promise<DownloadResponse> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/preview/${fileId}`;
        return requestHandler.get<DownloadResponse>(url);
    };

    const getFileStorageObject = async (fileId: string): Promise<StorageObject> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/file/${fileId}`;
        return requestHandler.get<StorageObject>(url);
    };

    const getFolderStorageObject = async (folderId: string): Promise<StorageObject> => {
        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/folder/${folderId}`;
        return requestHandler.get<StorageObject>(url);
    };

    const updateFolder = async (
        item: StorageObject | null | undefined
    ): Promise<StatusResponse> => {
        if ((item !== undefined) && item) {
            let url = `/${config.DLS_TENANT}/folder/${item.id}`;
            return requestHandler.put<StatusResponse>(url, JSON.stringify(item), ContentType.JSON);
        }

        return {status: '400', message: t('ERROR_FOLDER_ID_MISSING')} as StatusResponse;
    };

    const updateFile = async (
        thumbnail: File | null,
        item: StorageObject | null | undefined
    ): Promise<StatusResponse> => {
        if ((item !== undefined) && item) {
            let url = `/${config.DLS_TENANT}/file/${item.id}`;
            return await requestHandler.put<StatusResponse>(url, JSON.stringify(item), ContentType.JSON)
                .then(async () => await upsertThumbnail(item.id, thumbnail, item.metadata.thumbnailUrl));
        }

        return {status: '400', message: t('ERROR_FILE_ID_MISSING')} as StatusResponse;
    };

    const deleteFolder = async (
        folderId: string,
        countries: string[],
        languages: string[],
        locales: string[],
        isComplete: boolean
    ): Promise<StatusResponse> => {
        let formData = new FormData();
        formData.append('countries', JSON.stringify(countries));
        formData.append('languages', JSON.stringify(languages));
        formData.append('locales', JSON.stringify(locales));
        formData.append('isComplete', isComplete.toString());

        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/folder/delete/${folderId}`;
        return requestHandler.post(url, formData, ContentType.MULTIPART_FORMDATA);
    };

    const deleteFile = async (
        fileId: string,
        countries: string[],
        languages: string[],
        locales: string[],
        isComplete: boolean
    ): Promise<StatusResponse> => {
        let formData = new FormData();
        formData.append('countries', JSON.stringify(countries));
        formData.append('languages', JSON.stringify(languages));
        formData.append('locales', JSON.stringify(locales));
        formData.append('isComplete', isComplete.toString());

        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/file/delete/${fileId}`;
        return requestHandler.post(url, formData, ContentType.MULTIPART_FORMDATA);
    };

    const getWatermarkFileTypes = async (): Promise<string[]> => {
        let url = '/watermark/supported-file-types';
        return watermarkSvcRequestHandler.get<string[]>(url);
    };

    const markAsFavorite = async (
        resourceId: string,
        isFavorite: boolean
    ): Promise<StatusResponse> => {
        let formData = new FormData();
        if (isFavorite) {
            formData.append('isFavorite', isFavorite.toString());
        }

        let url = `/${config.DLS_TENANT}/${config.DLS_SPACE_NAME}/favorites/mark-favorite/${resourceId}`;
        return requestHandler.put<StatusResponse>(url, formData, ContentType.MULTIPART_FORMDATA);
    };

    const getStorageObjectTemplates = async (activeOnly: boolean): Promise<Template[]> => {
        let url = `/${config.DLS_TENANT}/templates/storage-object?activeOnly=${activeOnly}`;
        return requestHandler.get<Template[]>(url);
    };

    const getUiConfigurationTemplates = async (activeOnly: boolean): Promise<Template[]> => {
        let url = `/${config.DLS_TENANT}/templates/ui-configuration?activeOnly=${activeOnly}`;
        return requestHandler.get<Template[]>(url);
    };

    const getUiCombinedTemplates = async (activeOnly: boolean): Promise<Template[]> => {
        let url = `/${config.DLS_TENANT}/templates/ui-combined?activeOnly=${activeOnly}`;
        return requestHandler.get<Template[]>(url);
    };

    const getTemplates = async (activeOnly: boolean): Promise<Template[]> => {
        let url = `/${config.DLS_TENANT}/templates/all?activeOnly=${activeOnly}`;
        return requestHandler.get<Template[]>(url);
    };

    return {
        createFolder,
        uploadFile,
        getStorageObjects,
        getStorageObject,
        getBreadcrumbsObjects,
        getDisclaimerData,
        giveConsent,
        revokeConsent,
        downloadFile,
        downloadThumbnailFile,
        downloadThumbnail,
        downloadThumbnailUrl,
        upsertThumbnail,
        getPreviewUrl,
        getFileStorageObject,
        getFolderStorageObject,
        updateFolder,
        updateFile,
        deleteFolder,
        deleteFile,
        getWatermarkFileTypes,
        markAsFavorite,
        getLabelData,
        getStorageObjectTemplates,
        getUiConfigurationTemplates,
        getUiCombinedTemplates,
        getTemplates
    };
};